// « Ce que l'écran dit pendant qu'il attend » — lot V (retours réunion du 13/09).
//
// Trois mensonges à réparer, tous mesurés :
//
//  1. LE DÉLAI D'ATTENTE de la thématique était à 15 s. Mesuré en production le 13/09,
//     page Logement d'un abonné à Besançon : 22,42 s. L'écran coupait donc une page qui
//     MARCHAIT et affichait « Thématique indisponible » — un faux négatif. C'est le bug
//     que Cyrille a rapporté en croyant que le serveur saturait.
//  2. LE COMPTE À REBOURS des lectures repartait à 62 s, puis 25 s : le backend rend la
//     MÉDIANE VIVANTE des 60 dernières générations, recalculée à chaque sondage (5 s) —
//     elle bouge, et l'écran la reprenait comme si l'attente recommençait.
//  3. « Encore quelques instants » tournait un quart d'heure sur une génération qui
//     n'aurait jamais lieu (crédit Anthropic épuisé, ou plafond mensuel atteint).
//
// Ce module ne rend que des CHAÎNES et des NOMBRES : testable sans DOM, et il laisse
// `ThemeView` / `LectureTerobs` quasi intacts (le lot W y travaille en parallèle).

// ── 1. Le délai d'attente de la thématique ────────────────────────────────────────────
// Justifié par la mesure du 13/09 sur la production (Besançon/log, abonné), pas par une
// habitude. Trois régimes, parce que les trois existent vraiment :
//
//   serveur chauffé (le cas de production)   AVANT 19,12 s   APRÈS  7,48 s
//   processus froid, chauffe pas finie       AVANT 44,49 s   APRÈS 32,15 s
//   seconde visite                           AVANT  3,22 s   APRÈS  3,23 s
//
// (Marc avait relevé 22,42 s sur le serveur vivant : même régime que la ligne 1.)
//
// Le troisième régime commande. La chauffe du démarrage dure ~185 s, et pendant cette
// fenêtre — après chaque déploiement — une page peut coûter 32 s. 45 s couvre ce cas
// avec de la marge, tout en restant borné : le délai n'est plus là pour borner une
// lenteur NORMALE (c'est ce que faisaient les 15 s, et c'est ce qui coupait une page qui
// marchait) mais pour détecter une requête réellement perdue.
// Le confort, lui, vient de l'état d'attente ci-dessous — pas d'un délai plus court.
export const DELAI_THEME_MS = 45_000

// Au-delà de ce temps, on cesse de dire « chargement » et on dit ce qui se passe
// vraiment. 3 s : au-dessus de la page chaude (3,62 s mesurés hors réseau, donc en
// pratique la page arrive avant), en dessous du seuil où l'on croit à une panne.
export const SEUIL_PHASE_2_MS = 3_000

export const TEXTE_CHARGEMENT = 'Chargement de la thématique…'
// Dit la vérité sur ce qui coûte : les comparaisons EPCI / département / région /
// France de chaque indicateur. Un élu qui lit ça sait que la page travaille.
export const TEXTE_CALCUL = 'Calcul des comparaisons avec l’intercommunalité, le département, la région et la France…'

/** Le message d'erreur d'une thématique — et surtout : LENTEUR n'est pas PANNE.
 *
 *  `axios` rend `code === 'ECONNABORTED'` (ou `ETIMEDOUT`) sans réponse quand c'est LUI
 *  qui a coupé. Afficher « Thématique indisponible » dans ce cas était le faux négatif :
 *  la page existe, le serveur répond, il est seulement plus lent que notre patience. */
export function messageErreurTheme(err: {
  code?: string; response?: { status?: number; data?: { detail?: string } }
}): { titre: string; texte: string; lenteur: boolean } {
  const code = err?.response?.status
  if (!code && (err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT')) {
    return {
      titre: 'Cette thématique est plus longue que prévu',
      texte: 'Le serveur calcule encore les comparaisons de cette commune. '
        + 'La page n’est pas en panne — relancez l’affichage, elle sera plus rapide '
        + 'la seconde fois.',
      lenteur: true,
    }
  }
  if (code === 404) {
    return { titre: 'Thématique introuvable',
             texte: 'Cette thématique n’existe pas pour cette commune.', lenteur: false }
  }
  if (code === 429) {
    return { titre: 'Limite de consultation atteinte',
             texte: err?.response?.data?.detail
               || 'Limite quotidienne de consultation atteinte.', lenteur: false }
  }
  if (code) {
    return { titre: 'Thématique indisponible',
             texte: `Le serveur a répondu ${code}. Réessayez dans un instant.`,
             lenteur: false }
  }
  return { titre: 'Thématique indisponible',
           texte: 'Impossible de joindre le serveur. Vérifiez votre connexion, puis réessayez.',
           lenteur: false }
}

// ── 2. Le compte à rebours des lectures ───────────────────────────────────────────────
// Mesure du 13/09 sur `llm_commentaires.duree_s` : moyenne 57,5 s, médiane 53,7 s,
// maximum 138 s. Le décompte part donc de l'attente annoncée par le backend (sa médiane
// vivante) et, passé ce temps, il DIT que c'est plus long — il ne recommence pas.
/** Au-delà, on cesse de promettre une seconde précise : c'est la queue de distribution
 *  (le maximum mesuré est 138 s ; on arrondit au palier au-dessus). */
export const ATTENTE_LONGUE_S = 150
/** Repli quand le backend n'a encore rien mesuré — la MOYENNE du 13/09, pas un chiffre
 *  rond arbitraire : une page qui annonce 60 s parce que 60 est rond ment déjà un peu. */
export const ATTENTE_REPLI_S = 58

export interface LibelleAttente { texte: string; fort?: string }

/** Ce qu'affiche le bloc de lecture, à `ecouleS` secondes du début de L'ATTENTE.
 *
 *  `attenteS` est l'attente ANNONCÉE au moment où l'attente a commencé — figée par
 *  l'appelant. La passer à nouveau à chaque sondage est exactement ce qui faisait
 *  repartir le décompte de 62 s à 25 s sous les yeux de Cyrille. */
export function libelleAttente(attenteS: number | null, ecouleS: number): LibelleAttente {
  const attendu = attenteS ?? ATTENTE_REPLI_S
  const reste = Math.max(0, attendu - Math.floor(ecouleS))
  if (reste > 0) return { texte: 'Lecture en préparation —', fort: `environ ${reste} s` }
  if (ecouleS < ATTENTE_LONGUE_S) {
    // Le temps annoncé est passé : on le dit, on ne remet pas le compteur à zéro.
    return { texte: 'Lecture en préparation — c’est plus long que d’habitude,',
             fort: `${Math.floor(ecouleS)} s écoulées` }
  }
  // Au-delà du maximum jamais observé : on cesse de faire patienter sur une promesse.
  return { texte: 'La lecture prend un temps inhabituel. Les chiffres de la page, eux, '
           + 'sont complets — revenez la consulter plus tard.' }
}

// ── 3. Quand il n'y aura PAS de lecture ───────────────────────────────────────────────
export type EtatGeneration = 'en_cours' | 'api_gelee' | 'credit_epuise' | 'hors_espace' | 'plafond_atteint'

export interface Generation { etat?: EtatGeneration; message?: string }

// ── L'HORLOGE D'ATTENTE, PARTAGÉE PAR LA PAGE ─────────────────────────────────────────
// Le décompte était ancré dans CHAQUE bloc, par un `useRef` : un bloc qui se remonte
// (l'analyse d'ensemble bascule d'une branche de ternaire à l'autre dès qu'elle arrive)
// repartait du haut, et le chiffre annoncé par le backend — une médiane vivante,
// recalculée à chaque sondage — passait de 62 s à 25 s sous les yeux de Cyrille.
//
// Une attente est un fait de PAGE, pas de composant : l'ancre vit donc au niveau du
// module, armée une fois par (commune, cible) et jamais réarmée. État de module assumé —
// il modélise exactement ce que le commentaire d'origine demandait (« tous les décomptes
// d'un même chargement lisent la même horloge »), il est remis à zéro par le hook au
// changement de page, et il évite de faire transiter trois props de plus par `ThemeView`,
// dont le lot W a la charge en parallèle.
const _horloge: {
  cle: string; debut: number; annonce: number | null; generation: Generation | null
} = { cle: '', debut: 0, annonce: null, generation: null }

/** Arme l'horloge pour une cible. Idempotent : réappeler avec la même clé ne remet
 *  JAMAIS le départ à zéro, et le premier chiffre annoncé fait foi — c'est toute la
 *  correction. */
export function ancrerAttente(cle: string, annonce: number | null,
                              generation: Generation | null = null) {
  if (_horloge.cle !== cle) {
    _horloge.cle = cle; _horloge.debut = Date.now()
    _horloge.annonce = annonce; _horloge.generation = generation
    return
  }
  if (_horloge.annonce === null) _horloge.annonce = annonce
  _horloge.generation = generation ?? _horloge.generation
}

export function reinitialiserAttente() {
  _horloge.cle = ''; _horloge.debut = 0; _horloge.annonce = null; _horloge.generation = null
}

/** Ce que le composant doit lire : le départ (0 si non armée), l'attente annoncée au
 *  départ, et l'état de génération connu. */
export function horlogeAttente() {
  return { debut: _horloge.debut, annonce: _horloge.annonce, generation: _horloge.generation }
}

/** Vrai quand aucune génération n'aura lieu : plus de compte à rebours, un motif. */
export function generationBloquee(g: Generation | null | undefined): boolean {
  return g?.etat === 'credit_epuise' || g?.etat === 'hors_espace' || g?.etat === 'plafond_atteint'
    || g?.etat === 'api_gelee'
}

/** Le motif affiché. Le backend rend déjà la phrase (il connaît le plafond réel) ; ces
 *  replis ne servent qu'à une version d'API plus ancienne que cet écran. */
export function messageGeneration(g: Generation | null | undefined): string {
  if (g?.message) return g.message
  if (g?.etat === 'credit_epuise') {
    return 'La rédaction des lectures est momentanément suspendue. Les chiffres et les '
      + 'comparaisons de cette page, eux, sont à jour.'
  }
  if (g?.etat === 'hors_espace') {
    return 'Les lectures rédigées sont réservées aux territoires en démonstration, en essai ou '
      + 'abonnés. Les chiffres et les comparaisons de cette page sont à jour.'
  }
  return 'Le quota de rédaction du mois est atteint pour cette commune. Les chiffres et '
    + 'les comparaisons de cette page sont à jour.'
}
